import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrgClaims } from '@/lib/hooks/useOrgClaims';
import { Tabs } from '@/lib/types/tabs';
import { buildOrgPath, extractOrgId, stripOrgPrefix } from '@/lib/utils/orgRoutes';

// Single source of truth for route mappings
export const TAB_TO_ROUTE: Record<Tabs, string> = {
  [Tabs.HOME]: '/home',
  [Tabs.WORKSPACE]: '/workspace',
  [Tabs.NOTEBOOK]: '/notebook',
  [Tabs.ASSETS]: '/assets',
  [Tabs.SCENARIOS]: '/scenarios',
  [Tabs.USAGE]: '/usage',
  [Tabs.DOCS]: '/docs',
  [Tabs.ADMIN]: '/admin',
  [Tabs.ONTOLOGY]: '/ontology',
  [Tabs.SETTINGS]: '/settings',
  [Tabs.TEAM]: '/team',
};

export const ROUTE_TO_TAB: Record<string, Tabs> = {
  '/home': Tabs.HOME,
  '/workspace': Tabs.WORKSPACE,
  '/notebook': Tabs.NOTEBOOK,
  '/assets': Tabs.ASSETS,
  '/scenarios': Tabs.SCENARIOS,
  '/usage': Tabs.USAGE,
  '/docs': Tabs.DOCS,
  '/admin': Tabs.ADMIN,
  '/ontology': Tabs.ONTOLOGY,
  '/settings': Tabs.SETTINGS,
  '/team': Tabs.TEAM,
};

export interface NavigationContextValue {
  /** Current active tab */
  currentTab: Tabs;
  /** Switch to a different tab (updates both state and route) */
  switchTab: (tab: Tabs) => void;
  /** Whether the workspace tab has been opened at least once (for lazy-mounting stream) */
  hasOpenedWorkspace: boolean;
  /** Manually set the hasOpenedWorkspace flag */
  setHasOpenedWorkspace: (value: boolean) => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

interface NavigationProviderProps {
  children: ReactNode;
}

/**
 * Provider component that manages navigation/tab state.
 * Syncs tab state with react-router location and provides route mappings.
 */
export function NavigationProvider({ children }: NavigationProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId: claimsOrgId } = useOrgClaims();
  const [currentTab, setCurrentTab] = useState<Tabs>(Tabs.HOME);
  const [hasOpenedWorkspace, setHasOpenedWorkspace] = useState(false);

  // Switch active tab - updates state and navigates to the org-scoped route
  const switchTab = useCallback(
    (tab: Tabs) => {
      setCurrentTab(tab);

      // Track if WORKSPACE tab has been opened
      if (tab === Tabs.WORKSPACE) {
        setHasOpenedWorkspace(true);
      }

      // Navigate to the corresponding route, prefixed with the active org.
      // Prefer the claims org (the org the token actually backs) over the URL
      // org: during an unsettled cross-org window the URL may point at an org
      // the token isn't in yet, and trusting it would navigate deeper into the
      // wrong org and fight OrgUrlSync's normalization. Fall back to the URL org
      // only before claims have loaded.
      const route = TAB_TO_ROUTE[tab];
      const orgId = claimsOrgId ?? extractOrgId(location.pathname);
      if (route && orgId) {
        const target = buildOrgPath(orgId, route);
        if (location.pathname !== target) {
          navigate(target);
        }
      }
    },
    [navigate, location.pathname, claimsOrgId]
  );

  // Sync tab state with the URL - handles browser back/forward and direct URL
  // navigation. The org prefix is stripped before mapping to a tab; org-less /
  // root redirects are owned by OrgUrlSync.
  useEffect(() => {
    const tab = ROUTE_TO_TAB[stripOrgPrefix(location.pathname)];

    if (tab && tab !== currentTab) {
      setCurrentTab(tab);
      if (tab === Tabs.WORKSPACE) {
        setHasOpenedWorkspace(true);
      }
    }
  }, [location.pathname, currentTab]);

  const value = useMemo<NavigationContextValue>(
    () => ({
      currentTab,
      switchTab,
      hasOpenedWorkspace,
      setHasOpenedWorkspace,
    }),
    [currentTab, switchTab, hasOpenedWorkspace]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

/**
 * Hook to access navigation context.
 * Must be used within a NavigationProvider.
 */
export function useNavigation(): NavigationContextValue {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
